import { RemunerationType } from '$constants'
import {
  RemunerationTimelineRecordModel as RemunerationTimelineRecord,
  RemunerationTimelineRecordStaffRoleModel as RemunerationTimelineRecordStaffRole,
  SalaryWeekContainerModel as SalaryWeekContainer,
  IRemunerationTimelineRecordModel as IRemunerationTimelineRecord,
  IRemunerationTimelineRecordStaffRoleModel as IRemunerationTimelineRecordStaffRole,
  ISalaryWeekContainerModel as ISalaryWeekContainer,
  LeaveWeekCostModel as LeaveWeekCost,
  ILeaveWeekCostModel as ILeaveWeekCost,
  IRemunerationTimelineRecordVenueModel as IRemunerationTimelineRecordVenue,
  RemunerationTimelineRecordVenueModel as RemunerationTimelineRecordVenue,
} from '@loadedhub/core-api-sdk'
import { Nullable } from '../types'
import dayjs, { Dayjs } from 'dayjs'
import sortBy from 'lodash/sortBy'
import { formatCurrency } from 'src/components/common/helpers'
import useAppStore from '$appStore'

export {
  type IRemunerationTimelineRecord,
  RemunerationTimelineRecord,
  type IRemunerationTimelineRecordStaffRole,
  RemunerationTimelineRecordStaffRole,
  type ISalaryWeekContainer,
  SalaryWeekContainer,
  type ILeaveWeekCost,
  LeaveWeekCost,
  type IRemunerationTimelineRecordVenue,
  RemunerationTimelineRecordVenue,
}

export const sortRemunerationTimeline = (
  remunerationTimeline: IRemunerationTimelineRecord[]
): IRemunerationTimelineRecord[] => {
  return sortBy(remunerationTimeline, (remunerationTimelineRecord) => {
    // Check if validTo dates are null or undefined
    if (remunerationTimelineRecord.validTo == null) {
      // Place null values first
      return Number.NEGATIVE_INFINITY
    } else {
      // Sort by validTo dates in ascending order
      return -dayjs.utc(remunerationTimelineRecord.validTo).valueOf()
    }
  })
}

/**
 * Returns the salary or hourly rate value for the remuneration timeline
 * @param remunerationTimelineRecord
 * @returns The numeric rate value
 */
export const getRemunerationTimelineSalaryOrHourlyRate = (
  remunerationTimelineRecord?: Nullable<IRemunerationTimelineRecord>
): number => {
  if (!remunerationTimelineRecord) {
    return 0
  }

  const isHourly =
    remunerationTimelineRecord.type === RemunerationType.HourlyRate

  if (isHourly) {
    // If hourly rate type, find the default role's hourly rate
    const defaultRole = remunerationTimelineRecord.memberRoles
      ?.filter((row) => Object.hasOwn(row, 'index'))
      .find((role) => role.roleId === remunerationTimelineRecord.defaultRoleId)
    return defaultRole?.hourlyRate ?? 0
  }

  // If salary type, return the salary rate
  return remunerationTimelineRecord.salaryRate ?? 0
}

/**
 * Returns the formatted salary or hourly rate label
 * @param defaultPayRate The numeric pay rate value
 * @param isHourlyRate Denotes whether the pay rate is hourly rate (if false, then Salary)
 * @param hasSalaryPermission Whether user has salary permission
 * @param hasPayrollPermission Whether user has payroll permission
 * @param showAsHourly When true, displays salary amounts with "per hour" unit instead of "per annum"
 * @param showUnit Whether to show the unit label (per hour or per annum)
 * @returns The formatted label string
 */
export const getRemunerationSalaryOrHourlyRateLabel = (
  defaultPayRate: number | undefined,
  isHourlyRate: boolean,
  hasSalaryPermission: boolean,
  hasPayrollPermission: boolean,
  showAsHourly = false,
  showUnit = true
): string => {
  if (isHourlyRate) {
    if (hasPayrollPermission) {
      const formattedAmount = formatCurrency(defaultPayRate)
      return showUnit ? `${formattedAmount} per hour` : formattedAmount
    }
    return 'On Hourly'
  }

  if (hasSalaryPermission && hasPayrollPermission) {
    const formattedAmount = formatCurrency(defaultPayRate)
    if (!showUnit) {
      return formattedAmount
    }
    const unit = showAsHourly ? 'per hour' : 'per annum'
    return `${formattedAmount} ${unit}`
  }

  return 'On Salary'
}

/**
 * Get the default member role for a remuneration timeline
 * @param remunerationTimelineRecord
 * @returns
 */
export const getDefaultMemberRole = (
  remunerationTimelineRecord: Nullable<IRemunerationTimelineRecord>
) => {
  if (!remunerationTimelineRecord) return undefined

  return remunerationTimelineRecord.memberRoles?.find(
    (role) => role.roleId === remunerationTimelineRecord.defaultRoleId
  )
}

/**
 * Checks if a staff member is linked to a specific venue through their active
 * remuneration timeline record's venue assignments
 * @param remunerationTimeline the staff member's remuneration timeline
 * @param venueId the venue ID to check against
 * @returns true if the staff member is linked to the venue
 */
export const isLinkedToVenue = (
  remunerationTimeline: IRemunerationTimelineRecord[] | undefined,
  venueId?: string
): boolean => {
  const activeRecord = remunerationTimeline?.find((rt) => rt.isActive)
  const targetVenueId = venueId ?? useAppStore.getState().activeCompany?.id
  return (
    activeRecord?.venueAssignments?.some(
      (va) => va.venueId === targetVenueId
    ) ?? false
  )
}

/**
 * Finds the remuneration timeline record that includes the given reference date.
 * Compares validFrom/validTo as date-only strings against the reference date.
 * @param remunerationTimeline the staff member's remuneration timeline
 * @param referenceDate the date to match against
 * @returns the matching record, or undefined if none found
 */
export const getTimelineRecordForDate = (
  remunerationTimeline: IRemunerationTimelineRecord[] | undefined,
  referenceDate: Dayjs
): IRemunerationTimelineRecord | undefined => {
  if (!remunerationTimeline) return undefined

  const refDateString = referenceDate.format('YYYY-MM-DD')

  return remunerationTimeline.find((record) => {
    const isAfterValidFrom = refDateString >= record.validFrom
    const isBeforeValidTo = record.validTo
      ? refDateString < record.validTo
      : true

    return isAfterValidFrom && isBeforeValidTo
  })
}

/**
 * Finds the venue assignment for the current venue from the timeline record
 * that is valid for the given reference date
 * @param remunerationTimeline the staff member's remuneration timeline
 * @param referenceDate the date to match against
 * @param venueId optional venue ID to check against, defaults to active company
 */
export const getVenueAssignmentForDate = (
  remunerationTimeline: IRemunerationTimelineRecord[] | undefined,
  referenceDate: Dayjs,
  venueId?: string
) => {
  const record = getTimelineRecordForDate(remunerationTimeline, referenceDate)
  const targetVenueId = venueId ?? useAppStore.getState().activeCompany?.id
  return record?.venueAssignments?.find((va) => va.venueId === targetVenueId)
}
